/**
 * What appears where: the kiosk / non-kiosk contract (spec
 * docs/specs/2026-10-08-kiosk-web-split-design.md §2). This table is the single source of truth;
 * features ask `useSurface().can(capability)` (src/contexts/SurfaceContext.tsx) and never branch
 * on the domain type themselves. Pinned by surfacePolicy.test.ts.
 *
 * - `kiosk`: a kiosk domain (public touch machines). Account, money flows, History, Help work;
 *   nothing leaves the app (no Play, no external links, no app downloads, no side strip). Shared
 *   machines sign an idle Player out, send the heartbeat and keep the focused field above the
 *   on-screen keyboard.
 * - `mobile`: every non-kiosk deployment (web, phones, the installed PWA).
 * - Both known surfaces show the offline ("connecting") screen, as production did on every
 *   kiosk and mobile domain.
 * - `unknown`: no build flag and the server has not answered yet. Everything that is off on a
 *   kiosk is off, so nothing kiosk-forbidden flashes on a kiosk; the kiosk-only behaviours wait
 *   for the server too.
 */

export const SURFACES = ['kiosk', 'mobile', 'unknown'] as const;
export type Surface = (typeof SURFACES)[number];
/** A surface the server or a flag can name (`unknown` is only "not known yet"). */
export type KnownSurface = Exclude<Surface, 'unknown'>;

export const CAPABILITIES = [
  /** Play button on Your games cards, the game sheet and My games. */
  'play',
  /** Right-edge strip: support, Telegram, Android app, iPhone guide, tour, Install app. */
  'sideStrip',
  /** Android APK link, iPhone install guide, "Install app" (PWA). */
  'appDownloads',
  /** Telegram, game sites, "Open in new tab". */
  'externalLinks',
  /** 10 s idle, then a 20 s countdown, then sign-out. */
  'idleSignOut',
  /** POST /api/kiosk/heartbeat. */
  'heartbeat',
  /** Full-screen "On connecting" screen while the network or API is down. */
  'offlineScreen',
  /** Keep the focused field visible above the on-screen keyboard. */
  'keyboardAssist',
] as const;
export type Capability = (typeof CAPABILITIES)[number];
export type Capabilities = Readonly<Record<Capability, boolean>>;

const KIOSK: Capabilities = Object.freeze({
  play: false,
  sideStrip: false,
  appDownloads: false,
  externalLinks: false,
  idleSignOut: true,
  heartbeat: true,
  offlineScreen: true,
  keyboardAssist: true,
});

const MOBILE: Capabilities = Object.freeze({
  play: true,
  sideStrip: true,
  appDownloads: true,
  externalLinks: true,
  idleSignOut: false,
  heartbeat: false,
  offlineScreen: true,
  keyboardAssist: false,
});

const UNKNOWN: Capabilities = Object.freeze({
  play: false,
  sideStrip: false,
  appDownloads: false,
  externalLinks: false,
  idleSignOut: false,
  heartbeat: false,
  offlineScreen: false,
  keyboardAssist: false,
});

const POLICY: Readonly<Record<Surface, Capabilities>> = Object.freeze({ kiosk: KIOSK, mobile: MOBILE, unknown: UNKNOWN });

/** The capabilities of a surface (frozen; the §2 table). */
export function capabilitiesFor(surface: Surface): Capabilities {
  return POLICY[surface] ?? UNKNOWN;
}
